import { useCallback, useState } from "react";
import biApi from "../../../../../../api/biApi";
import type { ReportField, ValueCell } from "../../../../../../api/types/svTypes.ts";
import useChartStateViewer from "../../../../reporting/sv/components/viewer/chart/hooks/useChartStateViewer.ts";
import { useRequestConfigViewer } from "../../../../reporting/sv/components/viewer/chart/hooks/useRequestConfigViewer.ts";
import PreviewComponent from "../../../../reporting/sv/components/viewer/chart/PreviewComponent.tsx";
import type { ChartRunState } from "../../../../reporting/sv/components/viewer/storage/runState.ts";
import { useBiCommonDataContext } from "../../../../reporting/sv/contexts/BiCommonDataContext.ts";
import { generateRandomString } from "../../../utils/generateRandomString.ts";
import type { DrillDownArgs } from "../DrillDownArgs.ts";
import SmartViewWidgetViewerWrapper from "../SmartViewWidgetViewerWrapper";

interface Props {
  runState: ChartRunState;
  onDrillDown: (args: DrillDownArgs) => void;
}

const SmartViewChartWidgetViewer = ({ runState, onDrillDown }: Props) => {
  const [etag, setEtag] = useState<string | undefined>(undefined);
  const [sessionId] = useState<string>(generateRandomString);

  const { conditions, refreshToken } = useBiCommonDataContext();

  const { requestConfig: viewConfig } = useRequestConfigViewer({
    values: runState.values,
    isQueryLoggingActive: false,
    sessionId: sessionId,
  });

  const state = useChartStateViewer({
    dimensions: runState.dimensions,
    conditions,
    argumentFields: runState.arguments,
    values: runState.values,
    refreshToken: refreshToken,
    requestConfig: viewConfig,
    updateDevTools: undefined,
    setEtag: setEtag,
    runReport: biApi.dashboardReports.runChartReport,
  });

  const handleDrillDown = useCallback(
    (field: ReportField, value: ValueCell) => {
      onDrillDown({ field, cell: value, etag, sessionId });
    },
    [onDrillDown, etag, sessionId]
  );

  return (
    <SmartViewWidgetViewerWrapper initialized={state.initialized} validity={state.configurationValidity}>
      <PreviewComponent
        state={state}
        argumentFields={runState.arguments}
        values={runState.values}
        reportType={runState.reportType}
        settings={runState.settings}
        previewMode={"maximize"}
        onDrillDown={handleDrillDown}
      />
    </SmartViewWidgetViewerWrapper>
  );
};

export default SmartViewChartWidgetViewer;
